<script setup lang="ts">
import { get } from "http";
import { Bell } from "@element-plus/icons-vue";
import useDetectOutsideClick from "~/composable/useDetectOutsideClick.js";
import { useNotificationStore } from "~/stores/notificationStore";
import { useTopBarBadgeStore } from "~/stores/topBarBadgeStore";
import { NotificationStatus } from "~/types/enums/notificationStatus";
import { Notification } from "~/types/notification";
import { useAuthStore } from "~/stores/auth";
import { PermissionNames } from "~/configs/permission_names";
const authStore = useAuthStore();
const topBarBadgeStore = useTopBarBadgeStore();
const notificationTab = ref(false);
const notificationTabButton = ref(null);
const tab = ref("All");
const router = useRouter();
const notificationStore = useNotificationStore();
function capitalizeFirstLetter(str: string | null) {
  if (str?.length === 0) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}
// Query params each tab adds on top of the base request. "Profile Comment" is a
// slice of Comment rather than its own related_type: every mention notification
// is related to the comment, and only the payload says what was commented on.
const tabParams: Record<string, Record<string, string>> = {
  All: {},
  Unread: {},
  Lead: { related_type: "lead" },
  Ticket: { related_type: "ticket" },
  Opportunity: { related_type: "opportunity" },
  Task: { related_type: "task" },
  Comment: { related_type: "comment" },
  "Profile Comment": { related_type: "comment", commentable_type: "profile" },
};

const tabNames = Object.keys(tabParams);

function formatDateTime(dateTimeString: string): string {
  const date = new Date(dateTimeString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else {
    return `${diffInDays}d ago`;
  }
}

useDetectOutsideClick(notificationTabButton, () => {
  notificationTab.value = false;
  notificationStore.resetDateRange();
});
const toggleNotificationTab = async () => {
  notificationTab.value = !notificationTab.value;
  await getNotifications();
};
const getNotifications = async (status: NotificationStatus | null = null) => {
  if (notificationTab.value) {
    const params: Record<string, any> = {
      user_id: authStore.user?.id,
    };
    if (status !== null) params.status = status;
    Object.assign(params, tabParams[tab.value] ?? {});
    await notificationStore.getNotifications(params);
  }
};
const showMoreNotifications = async () => {
  const params: Record<string, any> = {
    user_id: authStore.user?.id,
  };

  if (tab.value === "Unread") {
    params.status = NotificationStatus.New;
  }

  Object.assign(params, tabParams[tab.value] ?? {});

  if (notificationStore.allNotifications.length > 0) {
    params.oldest_id =
      notificationStore.allNotifications[
        notificationStore.allNotifications.length - 1
      ].id;
  }

  await notificationStore.showMoreNotifications(params);
};

const getSvgName = (relatedType: string | null) => {
  switch (relatedType) {
    case "lead":
      return "user";
    case "opportunity":
      return "opportunities";
    case "task":
      return "task";
    case "comment":
      return "comment";
    default:
      return "clock";
  }
};
// "X has assigned N new leads to you" covers a whole import batch, so there is no
// single related model to open. The backend puts the agent, the batch id and the
// created-at window on the payload; the leads index hydrates its filters straight
// from the query string, so those params land the user on exactly those leads.
const getBulkLeadAssignmentLink = (payload: Record<string, any>) => {
  const query = new URLSearchParams({ show_type: "not_converted" });

  if (payload.assigned_users) {
    query.set("assigned_users", String(payload.assigned_users));
  }
  if (payload.batch_id) {
    query.set("batch_id", String(payload.batch_id));
  }
  if (payload.leads_created_at_from) {
    query.set("leads_created_at_from", String(payload.leads_created_at_from));
  }
  if (payload.leads_created_at_to) {
    query.set("leads_created_at_to", String(payload.leads_created_at_to));
  }

  return `/crm/leads?${query.toString()}`;
};

const isBulkLeadAssignment = (notification: Notification) => {
  const payload = notification.payload as Record<string, any> | null;

  return (
    !notification.related_type &&
    !!payload &&
    (!!payload.batch_id || !!payload.leads_created_at_from)
  );
};

const getLink = (notification: Notification) => {
  if (isBulkLeadAssignment(notification)) {
    return isUserPermitted(PermissionNames.LEAD_SHOW)
      ? getBulkLeadAssignmentLink(notification.payload as Record<string, any>)
      : "";
  }

  switch (notification.related_type) {
    case "lead":
      return PermissionNames.LEAD_SHOW
        ? `/crm/leads/${notification.related_id}`
        : "";
    case "opportunity":
      return PermissionNames.OPPORTUNITY_SHOW
        ? `/crm/opportunities/${notification.related_id}`
        : "";
    case "task":
      return PermissionNames.TASK_SHOW
        ? `/task-manager/tasks/${notification.related_id}`
        : "";
    case "comment":
      if (
        "commentable_id" in notification.payload &&
        "commentable_type" in notification.payload
      ) {
        return notification.payload.commentable_type === "task"
          ? PermissionNames.TASK_SHOW
            ? `/task-manager/tasks/${notification.payload.commentable_id}`
            : ""
          : notification.payload.commentable_type === "lead"
          ? PermissionNames.LEAD_SHOW
            ? `/crm/leads/${notification.payload.commentable_id}`
            : ""
          : notification.payload.commentable_type === "opportunity"
          ? PermissionNames.OPPORTUNITY_SHOW
            ? `/crm/opportunities/${notification.payload.commentable_id}`
            : ""
          : notification.payload.commentable_type === "profile"
          ? isUserPermitted(PermissionNames.PROFILE_SHOW)
            ? `/profiles/${notification.payload.commentable_id}`
            : ""
          : "";
      }
      return "";
    case "ticket":
      if (PermissionNames.TICKET_SHOW) {
        const status = null;
        const ticketId = notification.payload.id;
        return `/help-desk/ticket-list?status=4&status=5&status=1&status=3&id=${ticketId}&for_me=${false}`;
      }
      return "";
    default:
      return "";
  }
};

const getPayloadName = (payload: Notification["payload"]) => {
  if (payload.name) {
    return payload.name;
  } else if (payload.title) {
    return payload.title;
  } else if (payload.body) {
    return payload.body;
  }
  return null;
};
const markAllAsRead = async () => {
  const { error } = await notificationStore.bulkReadNotifications({ all: true });
  if (!error) {
    topBarBadgeStore.resetUnReadNotificationCount();
  }
};
const markAsRead = async (notification: Notification) => {
  if (notification.status !== NotificationStatus.New) return;
  const { error } = await notificationStore.bulkReadNotifications({
    ids: [notification.id],
  });
  if (!error) {
    topBarBadgeStore.decrementUnReadNotificationCount();
  }
};
const updateNotificationStatus = async (notification: Notification) => {
  if (notification.status === NotificationStatus.New) {
    await notificationStore.updateNotification(notification.id, {
      status: NotificationStatus.Read,
    });
    topBarBadgeStore.decrementUnReadNotificationCount();
  }
  notificationTab.value = !notificationTab.value;

  const link = getLink(notification);
  if (link) {
    router.push(link);
  }
};
function containsComment(text) {
  return text.toLowerCase().includes("comment");
}
</script>
<template>
  <div ref="notificationTabButton" class="relative grid">
    <NavButton
      class="relative border-[#3B2A8233]/[.2]"
      @click="toggleNotificationTab"
    >
      <Svgs
        name="bell"
        :class="
          notificationTab
            ? 'text-dark-text-blue dark:stroke-white'
            : 'text-white dark:stroke-white'
        "
        class="w-[17px] h-[16px] sm:text-black"
        stroke-width="1.7"
      />
      <Badge
        v-if="topBarBadgeStore.unReadNotificationCount > 0"
        class="top-[-8px] right-[-10px] absolute"
        >{{ topBarBadgeStore.unReadNotificationCount }}
      </Badge>
    </NavButton>

    <TransWrap :show="notificationTab">
      <PopUp
        v-show="notificationTab"
        class="notification-w max-h-[70vh] overflow-x-auto"
      >
        <div
          class="px-4 py-3 justify-between flex items-center sticky top-0 bg-white border-b dark:border-dark-bluegray-border dark:bg-dark-blue"
        >
          <span class="feature-container-title text-base font-bold"
            >Notifications</span
          >
          <!--Mark all as read button-->
          <button
            v-if="topBarBadgeStore.unReadNotificationCount > 0"
            class="text-light-purple-border text-sm font-semibold flex items-center gap-1 hover:text-light-purple transition-all"
            @click="markAllAsRead"
          >
            <Svgs name="tick" />
            <span> Mark all as read </span>
          </button>
        </div>

        <div class="grid gap-1 pt-1">
          <ul class="flex w-full gap-2 px-3 flex-wrap">
            <li
              v-for="tabName in tabNames"
              :key="tabName"
              :class="{
                'border-b border-light-sec-purple text-light-sec-purple':
                  tab === tabName,
              }"
              class="w-fit px-1 pb-1 info-header hover:cursor-pointer transition-all border-b hover:border-gray-500"
              @click.prevent="
                () => {
                  tab = tabName;
                  notificationStore.resetDateRange();
                  getNotifications(
                    tabName === 'Unread' ? NotificationStatus.New : null,
                  );
                }
              "
            >
              {{ tabName }}
            </li>
          </ul>

          <Loader v-if="notificationStore.isLoading" />

          <div
            v-else-if="
              !notificationStore.isLoading &&
              notificationStore.allNotifications.length > 0
            "
            class="grid gap-2 px-1 py-2"
          >
            <NuxtLink
              v-for="notification in notificationStore.allNotifications"
              :key="notification.id"
              class="flex gap-3 p-4 py-2 items-center group hover:bg-gray-100 rounded-md dark:hover:bg-light-purple-bar"
              @click="updateNotificationStatus(notification)"
            >
              <div class="p-3 bg-gray-200 rounded-full">
                <Svgs
                  :name="getSvgName(notification?.related_type)"
                  class="text-light-sec-purple"
                />
              </div>
              <div class="flex flex-col gap-1 w-full">
                <div
                  class="flex items-center justify-between feature-container-title font-semibold"
                >
                  <div class="flex gap-1 items-center">
                    <div
                      v-if="notification.status === NotificationStatus.New"
                      class="p-1 bg-light-sec-purple rounded-full"
                    ></div>
                    <h2 class="text-sm">
                      {{ notification.title }}
                    </h2>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      v-if="notification.status === NotificationStatus.New"
                      class="opacity-0 group-hover:opacity-100 text-light-purple-border hover:text-light-purple transition-all"
                      title="Mark as read"
                      @click.stop.prevent="markAsRead(notification)"
                    >
                      <Svgs name="presence-check" class="w-4 h-4" />
                    </button>
                    <span
                      class="text-xs font-normal text-light-text-purple dark:text-white"
                    >
                      {{ formatDateTime(notification.created_at) }}
                    </span>
                  </div>
                </div>
                <span
                  class="text-xs text-light-text-purple dark:text-light-font"
                >
                  {{ notification.body }}
                  <br />
                  <div
                    v-if="getPayloadName(notification.payload)"
                    class="flex gap-1 items-center"
                  >
                    <span>
                      {{
                        notification.related_type === "comment"
                          ? "Comment:"
                          : "Title:"
                      }}
                    </span>
                    <span v-html="getPayloadName(notification.payload)"></span>
                  </div>
                </span>
              </div>
            </NuxtLink>
          </div>
          <el-empty v-else description="No Notifications" :image-size="90" />
        </div>
        <Loader v-if="notificationStore.moreCommentsLoader" />

        <hr class="dark:border-dark-bluegray-border/[.4]" />

        <div
          v-if="notificationStore.notifications.length > 0"
          class="px-4 py-3 justify-between flex items-center"
        >
          <button
            class="feature-container-title text-sm font-bold"
            @click="showMoreNotifications"
          >
            Show More
          </button>
          <!--Mark all as read button-->
        </div>
      </PopUp>
    </TransWrap>
  </div>
</template>
<style scoped>
.notification-w {
  width: 300px !important;
}
@media (min-width: 640px) {
  .notification-w {
    width: 350px !important;
  }
}
</style>
